'use client'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Users2, BarChart3, Briefcase, Plane, TrendingUp, Handshake, Building2, PhoneCall, MessageCircle, ArrowRight } from 'lucide-react'
import type { Locale } from '@/lib/common-content'
import { ConsultationCTASection } from '@/components/ConsultationCTASection'
import { PageHeroEyebrow, pageHeroLeadClass, pageHeroTitleClass } from '@/components/PageHeroEyebrow'
import { PageDecor } from '@/components/PageDecor'

/** Next.js link with framer-motion hover effects (pre-loads the target page) */
const MotionLink = motion.create(Link)

const content = {
  en: {
    label: 'Business Program',
    title: 'Japanese for Business',
    description: 'Training, research and visits for Pakistani companies that work with Japan, or want to.',
    highlights: [
      { icon: 'growth', text: 'Japanese Market Intelligence' },
      { icon: 'partner', text: 'Japanese Corporate Training' },
      { icon: 'org', text: 'Japanese Delegation Visits' },
    ],
    ctaLabel: 'Next Step',
    formTitle: 'Capitalize on What Japan Has to Offer',
    formDesc: "Japan's economy continues to open doors for organizations ready to compete internationally — from market access to skilled talent pipelines. If your business is positioned to take advantage of this opportunity, share your details with us and our team will help you build the right plan.",
    btnLabel: 'Request a Consultation',
    callInstead: 'Or Call Us',
    phone: '+92 300 1301223',
    cards: [
      { title: 'Market Surveys & Analysis (Japan)', desc: 'Clear reports on your industry in Japan: trends, customers, competitors and how to enter the market.' },
      { title: 'Corporate Language Training', desc: 'Japanese classes built around your industry, so your team can talk to Japanese clients and partners.' },
      { title: 'Business & Cultural Etiquette', desc: 'How meetings, emails, business cards and decisions work in Japanese companies, and how to build trust.' },
      { title: 'Professional Delegation Visits', desc: 'Visits to Japan planned around your profession: companies, factories and partners you want to meet.' },
    ],
  },
  ja: {
    label: '法人向けプログラム',
    title: 'ビジネスのための日本語',
    description: '日本と取引がある、またはこれから始めたいパキスタン企業のための研修・調査・視察。',
    highlights: [
      { icon: 'growth', text: '日本市場インテリジェンス' },
      { icon: 'partner', text: '日本語法人研修' },
      { icon: 'org', text: '日本視察・派遣プログラム' },
    ],
    ctaLabel: '次のステップ',
    formTitle: '日本がもたらす好機を、貴社の力に',
    formDesc: '日本経済は、国際競争力を備えた組織に向けて、市場アクセスや優秀な人材へのパイプラインなど、多くの好機を提供し続けています。この機会を活かす準備がある企業様は、ぜひ情報をお寄せください。最適なプランをご提案いたします。',
    btnLabel: '無料相談を申し込む',
    callInstead: 'またはお電話で',
    phone: '+92 300 1301223',
    cards: [
      { title: '日本市場調査・分析', desc: '日本市場の業界トレンド、消費者動向、参入障壁の分析など、貴社の市場参入に向けた戦略的インテリジェンスを提供。' },
      { title: '法人向け語学研修', desc: '業界特性に合わせたカスタマイズ研修。貴社のチームが国際環境でプロとして通用する語学力を養います。' },
      { title: 'ビジネス文化・エチケット指導', desc: '日本のビジネスプロトコル、専門的な関係構築、企業開発に必要な倫理基準を徹底的に網羅。' },
      { title: 'プロフェッショナル訪問派遣', desc: '各個人のポートフォリオと専門性に最適化した日本への訪問プログラム。ネットワーキングを構築します。' },
    ],
  },
} as const

const HIGHLIGHT_ICONS: Record<string, typeof TrendingUp> = { growth: TrendingUp, partner: Handshake, org: Building2 }

const ICONS = [BarChart3, Users2, Briefcase, Plane]
const ICON_STYLES = [
  { text: "text-brand-charcoal", bg: "bg-brand-soft-gray", hoverBorder: "hover:border-brand-muted", ring: "group-hover:ring-brand-cool-gray" },
  { text: "text-brand-red", bg: "bg-brand-red-soft", hoverBorder: "hover:border-brand-red/40", ring: "group-hover:ring-brand-red-soft" },
  { text: "text-brand-green", bg: "bg-brand-green-soft", hoverBorder: "hover:border-brand-green/40", ring: "group-hover:ring-brand-green-soft" },
  { text: "text-brand-charcoal", bg: "bg-brand-soft-gray", hoverBorder: "hover:border-brand-muted", ring: "group-hover:ring-brand-cool-gray" },
]

export function BusinessPageView({ locale }: { locale: Locale }) {
  const copy = content[locale] || content['en']

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  }

  return (
    <div className="relative w-full py-12 md:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <PageDecor />

      <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative max-w-6xl mx-auto space-y-16">

        <motion.header variants={itemVariants} className="group text-center space-y-5">
          <PageHeroEyebrow>{copy.label}</PageHeroEyebrow>
          <h1 className={pageHeroTitleClass}>{copy.title}</h1>
          <p className={`${pageHeroLeadClass} max-w-xl`}>{copy.description}</p>

          <div className="flex flex-wrap justify-center gap-3 pt-2">
            {copy.highlights.map((h) => {
              const HIcon = HIGHLIGHT_ICONS[h.icon]
              return (
                <a key={h.text} href="#services" className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-brand-cool-gray shadow-sm text-sm font-semibold text-brand-charcoal-80 transition-all duration-200 ease-premium hover:border-brand-gold hover:text-brand-green hover:-translate-y-0.5 focus-ring">
                  <HIcon className="w-4 h-4 text-brand-green" />
                  {h.text}
                </a>
              )
            })}
          </div>
        </motion.header>

        {/* Divider to separate header from the offerings below */}
        <div className="flex items-center gap-4 max-w-3xl mx-auto">
          <span className="flex-1 h-px bg-brand-cool-gray" />
          <span className="w-2 h-2 rounded-full bg-brand-cool-gray" />
          <span className="flex-1 h-px bg-brand-cool-gray" />
        </div>

        <motion.div id="services" variants={containerVariants} className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto w-full scroll-mt-28">
          {(copy.cards || []).map((card, i) => {
            const Icon = ICONS[i]
            const style = ICON_STYLES[i]
            return (
              <MotionLink
                key={card.title}
                href={`/${locale}/contact?subject=${encodeURIComponent(card.title)}`}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className={`group relative flex flex-col p-8 bg-white border border-brand-cool-gray rounded-3xl shadow-sm hover:shadow-xl ${style.hoverBorder} transition-all duration-300 focus-ring`}
              >
                <span className="absolute top-6 right-7 text-xs font-bold text-brand-cool-gray tracking-widest">0{i + 1}</span>
                <div className={`mb-6 w-14 h-14 flex items-center justify-center rounded-2xl transition-all duration-300 ${style.bg} ring-4 ring-transparent ${style.ring} group-hover:scale-105`}>
                  <Icon className={`w-7 h-7 ${style.text}`} />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-brand-charcoal mb-3">{card.title}</h3>
                <p className="flex-1 text-brand-muted text-sm leading-relaxed font-light">{card.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">
                  {locale === 'ja' ? 'お問い合わせ' : 'Ask about this'}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </MotionLink>
            )
          })}
        </motion.div>

        <motion.div variants={itemVariants}>
          <ConsultationCTASection
          eyebrow={copy.ctaLabel}
          title={copy.formTitle}
          description={copy.formDesc}
          actions={
            <>
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="https://docs.google.com/forms/d/e/1FAIpQLScKKpL4OTStn6nbIVEu5WG2GF-MI0v3IHgb4O1X_jhd4Rj3bQ/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary gap-2 whitespace-nowrap"
              >
                <MessageCircle className="w-4 h-4" />
                {copy.btnLabel}
              </motion.a>

              <a
                href={`tel:${copy.phone.replace(/\s+/g, '')}`}
                className="flex items-center gap-3 px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-colors duration-300 whitespace-nowrap"
              >
                <span className="w-9 h-9 shrink-0 rounded-full bg-brand-green/15 flex items-center justify-center">
                  <PhoneCall className="w-4 h-4 text-brand-gold" />
                </span>
                <span className="flex flex-col leading-tight text-left">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-muted">
                    {copy.callInstead}
                  </span>
                  <span className="text-white font-bold text-sm">{copy.phone}</span>
                </span>
              </a>
            </>
          }
          />
        </motion.div>
      </motion.div>
    </div>
  )
}